import React from "react";
import { cn } from "utils/utils";
import { Eye, EyeOff } from "lucide-react";

/**
 * Skeleton loading component that matches the structure and dimensions of RosenCardPriceChart
 */
export function ChartSkeleton() {
  return (
    <aside aria-label="Price information">
      <div className="card w-full shadow-sm">
        <div className="card-body flex flex-col gap-0 p-3 md:p-4">
          {/* Header with title and controls */}
          <div
            className={cn(
              "flex items-center gap-2",
              "flex-col md:flex-row md:justify-between"
            )}
          >
            {/* Title */}
            <h2 className="text-base-content w-full text-sm font-medium md:text-base">
              Price Timeline
            </h2>

            {/* Finish controls are data-driven once the chart response loads. */}
            <div
              className={cn(
                "flex w-full items-center",
                "flex-col gap-1.5 md:flex-row md:justify-end md:gap-3"
              )}
            >
              <div className="flex flex-wrap justify-center gap-2 md:justify-end">
                <div className="skeleton h-5 w-28 rounded-full" />
              </div>
            </div>
          </div>

          {/* Chart area with loading spinner */}
          <div className="mt-2 md:mt-3">
            <div className="flex h-[340px] w-full items-center justify-center md:h-[380px]">
              <span className="loading loading-md loading-spinner text-primary"></span>
            </div>
          </div>

          {/* Chart legend matching ResponsiveChartLegend structure */}
          <div className="mt-2 flex flex-wrap items-center gap-1.5 px-2 md:mt-3 md:gap-2 md:px-0">
            {/* Price series - visible by default */}
            <div className="bg-base-200/30 flex items-center gap-1.5 rounded px-1.5 py-0.5 text-xs opacity-75 md:gap-2 md:px-2 md:py-1 md:text-sm">
              <div className="flex items-center space-x-1.5">
                <div className="bg-primary h-2 w-4 rounded-sm md:h-2.5 md:w-5" />
                <span className="text-base-content/80">Price</span>
              </div>
              <div className="text-base-content/50">
                <Eye size={14} />
              </div>
            </div>

            {/* MA7 series - hidden by default */}
            <div className="flex items-center gap-1.5 rounded bg-transparent px-1.5 py-0.5 text-xs opacity-40 md:gap-2 md:px-2 md:py-1 md:text-sm">
              <div className="flex items-center space-x-1.5">
                <div className="bg-secondary h-2 w-4 rounded-sm md:h-2.5 md:w-5" />
                <span className="text-base-content/80">MA7</span>
              </div>
              <div className="text-base-content/50">
                <EyeOff size={14} />
              </div>
            </div>

            {/* MA14 series - hidden by default */}
            <div className="flex items-center gap-1.5 rounded bg-transparent px-1.5 py-0.5 text-xs opacity-40 md:gap-2 md:px-2 md:py-1 md:text-sm">
              <div className="flex items-center space-x-1.5">
                <div className="bg-success h-2 w-4 rounded-sm md:h-2.5 md:w-5" />
                <span className="text-base-content/80">MA14</span>
              </div>
              <div className="text-base-content/50">
                <EyeOff size={14} />
              </div>
            </div>

            {/* MA30 series - hidden by default */}
            <div className="flex items-center gap-1.5 rounded bg-transparent px-1.5 py-0.5 text-xs opacity-40 md:gap-2 md:px-2 md:py-1 md:text-sm">
              <div className="flex items-center space-x-1.5">
                <div className="bg-warning h-2 w-4 rounded-sm md:h-2.5 md:w-5" />
                <span className="text-base-content/80">MA30</span>
              </div>
              <div className="text-base-content/50">
                <EyeOff size={14} />
              </div>
            </div>

            {/* Set Releases - visible by default */}
            <div className="bg-base-200/30 hover:bg-base-200/50 ml-auto flex items-center gap-1.5 rounded px-1.5 py-0.5 text-xs transition-colors md:gap-2 md:px-2 md:py-1 md:text-sm">
              <div className="flex items-center space-x-1.5">
                <div className="bg-secondary h-2 w-4 rounded-sm md:h-2.5 md:w-5" />
                <span className="text-base-content/80">Set Releases</span>
              </div>
              <div className="text-base-content/50">
                <Eye size={14} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}
